import { useState } from "react";
import { toast } from "sonner";
import { ArrowDownToLine } from "lucide-react";
import { api } from "@/lib/api";

/**
 * iter87.491 — Relocated out of the Event Bindings card (that card is now
 * Golf-only; these five templates never needed an Event Binding — each
 * has a hardcoded API-Football league_id/season). Pushes fresh cached
 * vendor data into every existing rivalry already running this template.
 */
const CASCADE_TEMPLATES = [
    { key: "world_cup_api", label: "FIFA World Cup 2026", meta: "API-Football · league 1 · 2026" },
    { key: "world_cup_ko_api", label: "World Cup — Knockout Only", meta: "Same event pool as World Cup" },
    { key: "premier_league_api", label: "Premier League", meta: "API-Football · league 39" },
    { key: "la_liga_api", label: "La Liga", meta: "API-Football · league 140" },
    { key: "champions_league_api", label: "Champions League", meta: "API-Football · league 2" },
];

export default function VendorDataCascadeCard() {
    const [busyKey, setBusyKey] = useState(null);

    const cascadeTo = async (tplKey, label) => {
        setBusyKey(tplKey);
        try {
            const prev = await api.siteApifCascadePreview(tplKey);
            const n = (prev.eligible || []).length;
            const skipped = (prev.skipped_draft_started || []).length;
            const skipNote = skipped ? ` (${skipped} skipped — draft already started)` : "";
            if (!n) {
                toast.info(`No eligible rivalries for ${label}${skipNote}`);
                return;
            }
            if (!window.confirm(`Cascade fresh ${label} data into ${n} rivalry tournament(s)?${skipNote}`)) return;
            const res = await api.siteApifCascade(tplKey, { confirm: true });
            const errs = (res.errors || []).length;
            toast.success(`Cascade complete — ${(res.updated || []).length} updated${errs ? `, ${errs} errors` : ""}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cascade failed");
        } finally {
            setBusyKey(null);
        }
    };

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="vendor-data-cascade-card">
            <div>
                <h3 className="font-display font-extrabold text-sm uppercase">Vendor Data Cascade</h3>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                    Force-pushes the latest cached vendor data into every existing rivalry already
                    running one of these templates. Skips any rivalry whose draft has already started.
                </p>
            </div>
            <div className="space-y-1.5">
                {CASCADE_TEMPLATES.map((t) => (
                    <div key={t.key} className="flex items-center gap-2 border border-[#222] px-3 py-2 text-xs" data-testid={`cascade-row-${t.key}`}>
                        <span className="font-bold text-zinc-200">{t.label}</span>
                        <span className="text-zinc-500 font-mono">{t.meta}</span>
                        <span className="flex-1" />
                        <button
                            data-testid={`cascade-btn-${t.key}`}
                            onClick={() => cascadeTo(t.key, t.label)}
                            disabled={busyKey === t.key}
                            className="pill text-[9px] px-2 py-0.5 border bg-blaze/10 border-blaze/60 text-blaze hover:bg-blaze/20 flex items-center gap-1 disabled:opacity-40"
                        >
                            <ArrowDownToLine className="w-3 h-3" /> {busyKey === t.key ? "…" : "CASCADE"}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
